import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { CloseIcon } from '@/components/ui/Icons';

interface Props {
  side: 'cart' | 'menu';
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}

/** Slide-in panel with a focus trap, Escape to close, and focus returned to the opener. */
export function Drawer({ side, open, title, onClose, children, footer }: Props) {
  const ref = useRef<HTMLElement>(null);
  const close = useCallback(() => onClose(), [onClose]);
  useFocusTrap(ref, open, close);
  // inert keeps a closed drawer's buttons out of the tab order (set directly; React 18 has no inert prop).
  useEffect(() => { if (ref.current) ref.current.inert = !open; }, [open]);

  return (
    <aside
      ref={ref}
      className={`drawer ${side}` + (open ? ' on' : '')}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      aria-hidden={!open}
    >
      <div className="dhead">
        {title}
        <button onClick={onClose} aria-label={`Close ${title.toLowerCase()}`}><CloseIcon size={14} /></button>
      </div>
      {side === 'menu' ? children : <div className="dbody">{children}</div>}
      {footer && <div className="dfoot">{footer}</div>}
    </aside>
  );
}
